/**
 * Canonical **upstream → downstream** order for **`public.station.name`** on devices (Alternates swap-out UX, etc.).
 * For **`public.station.id`** order on production calendar batch cards and Station WIP reporting, use
 * **`@/lib/sgsProductionCalendarStationOrder`** instead.
 * Keep aligned conceptually with line hubs in **`src/api/manufacturing-reporting-routes.ts`** (**`MANUFACTURING_LINE_STATIONS`**)
 * plus common SGS **hold / rework** stations (**Quality Control**, **Root Cause Analysis**) and **`Pre-Pack`** / **shipping** tails.
 *
 * Names are matched after **trim**, **collapse internal whitespace**, and **ASCII lowercase**. Empty values map to **`"no station"`**.
 * Aliases (**`SYNONYMS`**) fold legacy spellings onto one progression slot. Any other name ranks **after** this list,
 * sorted **alphabetically** by the canonical key so behavior stays stable across reloads **without** implying true floor ordering.
 */

import { publicStationSortOrderForName } from "@/lib/sgsProductionCalendarStationOrder";

function collapseWhitespace(value: string): string {
  return value.trim().replace(/\s+/g, " ").trim();
}

/** Normalized **`public.station.name`** buckets for progression (lowercase collapsed string). */
const SWAP_OUT_FLOOR_SEQUENCE: readonly string[] = [
  "no station",
  "inventory",
  "warehouse",
  "production receiving",
  "kitting",
  "config",
  "configuration",
  "config",
  "assembly",
  "calibration",
  "cal+test",
  "cal-test",
  "pre-encapsulation",
  "potting",
  "encapsulation",
  "wireless bench",
  "wireless final qc",
  "final qc",
  "final quality control",
  "quality assurance",
  "quality control",
  "engineering",
  "root cause analysis",
  "pre-pack",
  "pack out",
  "shipping team",
  "shipping",
  "shipped",
];

const SWAP_OUT_RANK = new Map<string, number>(
  SWAP_OUT_FLOOR_SEQUENCE.map((name, ordinal) => [name, ordinal]),
);

/** Extra spellings folded into **`SWAP_OUT_FLOOR_SEQUENCE`** keys. All keys lowercase collapsed. */
const SWAP_OUT_SYNONYMS: Readonly<Record<string, string>> = {
  "cal test": "cal+test",
};

const UNKNOWN_STATION_BUCKET_START = SWAP_OUT_FLOOR_SEQUENCE.length;

type ProgressionTier = { bucket: number; tieBreaker: string };

function progressionTierForKey(canonicalLower: string): ProgressionTier {
  const fromTable = publicStationSortOrderForName(canonicalLower);
  if (fromTable != null) return { bucket: fromTable, tieBreaker: "" };
  const mapped = SWAP_OUT_RANK.get(canonicalLower);
  if (mapped !== undefined) {
    return { bucket: mapped, tieBreaker: "" };
  }
  return { bucket: UNKNOWN_STATION_BUCKET_START, tieBreaker: canonicalLower };
}

/** Normalized lowercase key used for grouping, filters, and rank (blank / null → **`"no station"`**). */
export function swapOutCanonStationProgressionKey(stationName: string | null | undefined): string {
  const collapsed = collapseWhitespace(stationName ?? "");
  const lower = collapsed.toLowerCase();
  if (lower === "") return "no station";
  return SWAP_OUT_SYNONYMS[lower] ?? lower;
}

/** Heading text for grouped UI (**`No station`** when missing). Preserves casing from **`station.name`** where present. */
export function swapOutStationHeadingLabel(stationName: string | null | undefined): string {
  const collapsed = collapseWhitespace(stationName ?? "");
  return collapsed === "" ? "No station" : collapsed;
}

/** Sort replaceable **`serial_fci`** rows by floor progression then serial label, then **`id`** (numeric when possible). */
export function compareReplaceableSerialSwapOrder<
  Row extends {
    stationName?: string | null;
    deviceLabelSerial?: string | null;
    boxSerial?: string | null;
    id?: number | string | null;
  },
>(left: Row, right: Row): number {
  const keyLeft = swapOutCanonStationProgressionKey(left.stationName);
  const keyRight = swapOutCanonStationProgressionKey(right.stationName);
  const tierLeft = progressionTierForKey(keyLeft);
  const tierRight = progressionTierForKey(keyRight);
  if (tierLeft.bucket !== tierRight.bucket) {
    return tierLeft.bucket - tierRight.bucket;
  }
  if (tierLeft.bucket >= UNKNOWN_STATION_BUCKET_START && tierLeft.tieBreaker !== tierRight.tieBreaker) {
    return tierLeft.tieBreaker.localeCompare(tierRight.tieBreaker);
  }
  const labelLeft =
    left.deviceLabelSerial?.trim() || left.boxSerial?.trim() || (left.id == null ? "" : String(left.id));
  const labelRight =
    right.deviceLabelSerial?.trim() || right.boxSerial?.trim() || (right.id == null ? "" : String(right.id));
  const labelCompare = labelLeft.localeCompare(labelRight, undefined, { sensitivity: "base" });
  if (labelCompare !== 0) return labelCompare;

  const idLeftNumeric = typeof left.id === "number" ? left.id : typeof left.id === "string" ? Number(left.id) : Number.NaN;
  const idRightNumeric =
    typeof right.id === "number" ? right.id : typeof right.id === "string" ? Number(right.id) : Number.NaN;
  const bothNumeric = Number.isFinite(idLeftNumeric) && Number.isFinite(idRightNumeric);
  if (bothNumeric && idLeftNumeric !== idRightNumeric) {
    return idLeftNumeric - idRightNumeric;
  }
  return String(left.id ?? "").localeCompare(String(right.id ?? ""), undefined, { sensitivity: "base" });
}

export function compareSwapOutStationGroupHeadings<
  Row extends {
    progressionKey: string;
    headingLabel: string;
  },
>(left: Row, right: Row): number {
  const tierLeft = progressionTierForKey(left.progressionKey);
  const tierRight = progressionTierForKey(right.progressionKey);
  if (tierLeft.bucket !== tierRight.bucket) {
    return tierLeft.bucket - tierRight.bucket;
  }
  if (tierLeft.bucket >= UNKNOWN_STATION_BUCKET_START && tierLeft.tieBreaker !== tierRight.tieBreaker) {
    return tierLeft.tieBreaker.localeCompare(tierRight.tieBreaker);
  }
  return left.headingLabel.localeCompare(right.headingLabel, undefined, { sensitivity: "base" });
}
